import {
  doc,
  getDoc,
  collection,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * 폰트 검사 메타데이터 로드 (total_results만)
 * @param {string} docId - PPT 문서 ID
 * @returns {Promise<Object|null>} 메타데이터 또는 null
 */
export const loadFontResultsMeta = async (docId) => {
  try {
    const resultRef = doc(db, 'PPT_FILE', docId, 'font_results', 'total_results');
    const resultSnap = await getDoc(resultRef);

    if (!resultSnap.exists()) {
      return null;
    }

    const metaData = resultSnap.data();
    return {
      total_slides: metaData.total_slides || 0,
      total_violations: metaData.total_violations || 0,
      brand_fonts: metaData.brand_fonts || [],
      slide_width: metaData.slide_width || 0,
      slide_height: metaData.slide_height || 0
    };
  } catch (error) {
    console.error('[fontResultService] 메타데이터 로드 오류:', error);
    return null;
  }
};

/**
 * 특정 슬라이드의 폰트 검사 결과 로드 (lazy loading)
 * @param {string} docId - PPT 문서 ID
 * @param {number} slideIndex - 슬라이드 인덱스
 * @returns {Promise<Object|null>} 슬라이드 결과 또는 null
 */
export const loadSlideFontResult = async (docId, slideIndex) => {
  try {
    const slideRef = doc(db, 'PPT_FILE', docId, 'font_results', slideIndex.toString());
    const slideSnap = await getDoc(slideRef);

    if (!slideSnap.exists()) {
      return null;
    }

    const data = slideSnap.data();
    return {
      slide_index: slideIndex,
      has_violations: data.has_violations || false,
      violations_count: data.violations_count || 0,
      violations: data.violations || []
    };
  } catch (error) {
    console.error(`[fontResultService] 슬라이드 ${slideIndex} 결과 로드 오류:`, error);
    return null;
  }
};

/**
 * 폰트 검사 결과를 Firestore에서 로드하는 함수 (전체)
 * - total_results에서 메타데이터 로드
 * - 개별 슬라이드 문서('0', '1', ...)에서 slide_results 취합
 * @param {string} docId - PPT 문서 ID
 */
export const loadFontResults = async (docId) => {
  try {
    // 1. total_results에서 메타데이터 로드
    const metaData = await loadFontResultsMeta(docId);

    if (!metaData || metaData.total_slides === 0) {
      return null;
    }

    const slideCount = metaData.total_slides;

    // 2. 개별 슬라이드 문서에서 slide_results 병렬 로드
    const slidePromises = [];
    for (let i = 0; i < slideCount; i++) {
      slidePromises.push(loadSlideFontResult(docId, i));
    }

    const slideResults = (await Promise.all(slidePromises)).filter(r => r !== null);

    // 3. 메타데이터 + slide_results 합쳐서 반환
    return {
      ...metaData,
      slide_results: slideResults
    };
  } catch (error) {
    console.error('[fontResultService] 폰트 결과 로드 오류:', error);
    throw error;
  }
};

/**
 * 폰트 분석 진행상황을 실시간으로 구독하는 함수
 * @param {string} docId - PPT 문서 ID
 * @param {Function} callback - 진행상황 업데이트 콜백
 * @returns {Function} 구독 해제 함수
 */
export const subscribeToFontAnalysisProgress = (docId, callback) => {
  try {
    const docRef = doc(db, 'PPT_FILE', docId);
    const progressRef = doc(collection(docRef, 'font_results'), 'analysis_progress');

    const unsubscribe = onSnapshot(progressRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        callback({
          status: data.status || '',
          percent: data.percent || 0,
          completed: data.completed || false,
          total_violations: data.total_violations || 0,
          error: data.error || null
        });
      } else {
        callback({
          status: '',
          percent: 0,
          completed: false,
          error: null
        });
      }
    }, (error) => {
      console.error('폰트 분석 진행상황 구독 오류:', error);
      callback({ error: error.message });
    });

    return unsubscribe;
  } catch (error) {
    console.error('폰트 분석 진행상황 구독 설정 오류:', error);
    return null;
  }
};
